import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import withPermission from '../../../utils/withPermission';
import Swal from 'sweetalert2';
import CreatableSelect from 'react-select/creatable';
import { FaPlus } from 'react-icons/fa';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/deductions`;
const CATEGORY_API_URL = `${backendUrl}/api/categories?type=expense`;
const webzashUrl = process.env.REACT_APP_WEBZASH_URL || 'http://localhost/webzash-integration'; // Adjust as needed

const DeductionList = () => {
  const [deductions, setdeductions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [isEdit, setIsEdit] = useState(false);
  const [selecteddeduction, setSelecteddeduction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const debounceTimeoutRef = useRef(null);

  const WithEditPermission = withPermission(({ children }) => children, ["deduction-edit"]);
  const WithDeletePermission = withPermission(({ children }) => children, ["deduction-delete"]);
  const WithCreatePermission = withPermission(({ children }) => children, ["deduction-create"]);

  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      fetchdeductions(1, searchQuery);
    }, searchQuery ? 1500 : 0);

    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchQuery]);

  const fetchdeductions = async (page = 1) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("user-info"));
      const origin = localStorage.getItem("user-origin");
      const token = userInfo?.token;

      const response = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin },
        params: { page, search: searchQuery },
      });

      setdeductions(response.data.data.data || []);
      setPageCount(response.data.data.last_page);
      setCurrentPage(response.data.data.current_page);
    } catch (error) {
      console.error("Error fetching deductions:", error);
    }
  };

  const handleAdddeduction = () => {
    setFormData({ name: "", description: "" });
    setIsEdit(false);
    setSelecteddeduction(null);
    setShowModal(true);
    setErrors({});
  };

  const handleEditdeduction = (deduction) => {
    setFormData({ name: deduction.name, description: deduction.description });
    setIsEdit(true);
    setSelecteddeduction(deduction);
    setShowModal(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    return newErrors;
  };

  const handleSubmit = async () => {
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const origin = localStorage.getItem("user-origin");
    const token = userInfo?.token;
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (isEdit && selecteddeduction?.id) {
        const response = await axios.put(`${API_URL}/${selecteddeduction.id}`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin },
        });

        setdeductions(
          deductions.map((item) => (item.id === selecteddeduction.id ? response.data.data : item))
        );
      } else {
        const response = await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin },
        });

        setdeductions([...deductions, response.data.data]);
      }

      setShowModal(false);
    } catch (error) {
      console.error("Error saving deduction:", error);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won’t be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userInfo = JSON.parse(localStorage.getItem("user-info"));
          const origin = localStorage.getItem("user-origin");
          const token = userInfo?.token;

          await axios.delete(`${API_URL}/${id}`, {
            headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin },
          });

          setdeductions(deductions.filter((item) => item.id !== id));
          Swal.fire("Deleted!", "The deduction has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting deduction:", error);
          Swal.fire("Error!", "Failed to delete deduction.", "error");
        }
      }
    });
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
    fetchdeductions(newPage);
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">deduction</h4>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <div className="search-box me-2 mb-2 d-inline-block">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control font-size-12"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <i className="bx bx-search-alt search-icon" />
                      </div>
                    </div>
                    <WithCreatePermission>
                      <button
                        type="button"
                        className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                        onClick={handleAdddeduction}
                        data-bs-toggle="tooltip"
                        title="Add deduction"
                      >
                        <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                      </button>
                    </WithCreatePermission>
                  </div>

                  <table className="table align-middle table-nowrap table-check table-list">
                    <thead className="table-sales-gradient">
                      <tr>
                        <th style={{ width: "20px" }}></th>
                        <th>#</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductions.length > 0 ? (
                        deductions.map((item, index) => (
                          <tr key={item.id}>
                            <td></td>
                            <td>{index + 1}</td>
                            <td>{item.name}</td>
                            <td>{item.description}</td>
                            <td>
                              <div className="dropdown">
                                <i
                                  className="mdi mdi-dots-horizontal font-size-18"
                                  data-bs-toggle="dropdown"
                                  style={{ cursor: "pointer" }}
                                ></i>
                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                  <li>
                                    <WithEditPermission>
                                      <button className="dropdown-item" onClick={() => handleEditdeduction(item)}>
                                        Edit
                                      </button>
                                    </WithEditPermission>
                                  </li>
                                  <li>
                                    <WithDeletePermission>
                                      <button className="dropdown-item" onClick={() => handleDelete(item.id)}>
                                        Delete
                                      </button>
                                    </WithDeletePermission>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No deductions found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-end pagination-sm">
                    <ReactPaginate
                      previousLabel={"<"}
                      nextLabel={">"}
                      breakLabel={"..."}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={3}
                      onPageChange={handlePageChange}
                      containerClassName={"pagination pagination-rounded justify-content-end mb-2"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      activeClassName={"active"}
                    />
                  </div>

                  {/* Add/Edit Modal */}
                  <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement="end" style={{ width: "480px" }}>
                    <Offcanvas.Header closeButton>
                      <Offcanvas.Title>{isEdit ? "Edit deduction" : "Add deduction"}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                      <Form>
                        <Form.Group controlId="deductionName" className="mb-3">
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={errors?.name ? "is-invalid" : ""}
                          />
                          {errors.name && <div className="invalid-feedback">*{errors.name}</div>}
                        </Form.Group>

                        <Form.Group controlId="deductionDesc" className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            style={{ minHeight: "100px" }}
                          />
                        </Form.Group>
                      </Form>
                    </Offcanvas.Body>
                    <div className="p-3 d-flex justify-content-end border-top">
                      <Button variant="secondary me-2" onClick={() => setShowModal(false)}>
                        Close
                      </Button>
                      <Button variant="secondary" onClick={handleSubmit}>
                        {isEdit ? "Update" : "Save"}
                      </Button>
                    </div>
                  </Offcanvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default DeductionList;

